import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  loginUser,
  kakaoLoginWithProfile,
  googleLoginWithProfile,
  updateGender,
} from "../../api/userApi";
import { useJWT } from "../../contexts/JWTContext";
import { useGoogleLogin } from "@react-oauth/google";

import Toast from "../../components/common/Toast";
import { useToast } from "../../contexts/ToastContext";

export default function Login() {
  const [formData, setFormData] = useState({
    userId: "",
    password: "",
  });

  const navigate = useNavigate();
  const { login } = useJWT();
  const { showToast } = useToast();
  const [errors, setErrors] = useState({});
  const [focusedField, setFocusedField] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [showGenderModal, setShowGenderModal] = useState(false);
  const [gender, setGender] = useState("");

  // 일반 로그인
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {};
    if (!formData.userId.trim()) newErrors.userId = "아이디를 입력해주세요";
    if (!formData.password.trim())
      newErrors.password = "비밀번호를 입력해주세요";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      showToast("아이디/비밀번호를 확인해주세요.", "w");
      return;
    }

    try {
      const data = await loginUser(formData);
      console.log("JWT 로그인 성공:", data);

      login(data.accessToken);

      navigate("/home");
    } catch (error) {
      console.error("로그인 실패:", error);
      showToast("아이디 또는 비밀번호를 확인해주세요.", "e");
    }
  };

  useEffect(() => {
    if (!window.Kakao) return;

    if (!window.Kakao.isInitialized()) {
      window.Kakao.init("325dfc210d9fd1889a49de693e2d8c37");
    }
  }, []);

  // 카카오 로그인 (리다이렉트 방식)
  const handleKakaoLogin = () => {
    if (!window.Kakao) {
      alert("카카오 SDK 로드 실패");
      return;
    }

    window.Kakao.Auth.login({
      scope: "profile_nickname",
      success: function (authObj) {
        // access_token 사용해서 유저정보 요청
        window.Kakao.API.request({
          url: "/v2/user/me",
          success: async function (res) {
            const kakaoId = res.id; // 고유 ID
            const nickname = res.properties?.nickname || "카카오사용자";

            try {
              const data = await kakaoLoginWithProfile(kakaoId, nickname);

              login(data.accessToken);
              if (data.showGenderPopup) {
                setGender("");
                setShowGenderModal(true);
                showToast("추가 정보 입력이 필요해요.", "w");
              } else {
                navigate("/home");
              }
            } catch (e) {
              console.error("카카오 로그인 실패:", e);
              showToast("카카오 로그인 중 오류가 발생했습니다.", "e");
            }
          },
          fail: function (err) {
            console.error("카카오 사용자 정보 조회 실패:", err);
            showToast("카카오 사용자 정보 조회에 실패했습니다.", "e");
          },
        });
      },
      fail: function (err) {
        console.error("카카오 로그인 실패:", err);
        showToast("카카오 로그인에 실패했습니다.", "e");
      },
    });
  };

  const handleSignUp = () => {
    navigate("/signup");
  };

  // 구글 로그인
  const startGoogleLogin = useGoogleLogin({
    scope: "openid profile email",
    onSuccess: async (tokenResponse) => {
      try {
        const accessToken = tokenResponse.access_token;

        // 1) 프론트에서 구글 유저정보 조회 (백엔드 말고 브라우저 → 구글)
        const res = await fetch(
          "https://www.googleapis.com/oauth2/v3/userinfo",
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );

        if (!res.ok) {
          throw new Error("구글 사용자 정보 조회 실패");
        }

        const profile = await res.json();
        // profile.sub : 구글 고유 ID
        // profile.name : 프로필 이름

        const googleId = profile.sub;
        const nickname = profile.name;

        // 2) 이제 우리 서버로 ID + 닉네임만 보내기
        const data = await googleLoginWithProfile(googleId, nickname);

        // 3) 로그인 처리 (JWT 저장 / 컨텍스트 업데이트)
        login(data.accessToken);
        if (data.showGenderPopup) {
          setGender("");
          setShowGenderModal(true);
          showToast("추가 정보 입력이 필요해요.", "w");
        } else {
          navigate("/home");
        }
      } catch (err) {
        console.error("구글 로그인 실패:", err);
        showToast("구글 로그인 중 오류가 발생했습니다.", "e");
      }
    },
    onError: (err) => {
      console.error("구글 로그인 팝업 에러:", err);
      showToast("구글 로그인 팝업 에러가 발생했습니다.", "e");
    },
  });

  const handleGoogleLogin = () => {
    setShowGenderModal(false);
    startGoogleLogin();
  };

  // 성별 저장
  const handleGenderSubmit = async () => {
    if (gender === "") {
      showToast("성별을 선택해주세요.", "w");
      return;
    }

    try {
      await updateGender(gender);
      setShowGenderModal(false);
      navigate("/home");
    } catch (e) {
      console.error("성별 저장 실패:", e);
      showToast("성별 저장 중 오류가 발생했습니다.", "e");
    }
  };

  const handleBack = () => window.history.back();

  const isFormValid = formData.userId && formData.password;

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Toast />

      {/* ----------------------- */}
      {/* 🔹 상단 헤더      */}
      {/* ----------------------- */}
      <div className="px-6 py-6 flex items-center justify-between">
        <button
          onClick={handleBack}
          className="p-2 -ml-2 hover:bg-gray-100 rounded-full transition active:scale-95"
        >
          <svg
            className="w-6 h-6 text-gray-700"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2.5}
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>

        <h1
          className="text-2xl font-extrabold tracking-wide"
          style={{ color: "rgb(0, 224, 198)" }}
        >
          WINGSTEP
        </h1>

        <div className="w-6"></div>
      </div>

      {/* ----------------------- */}
      {/* 🔹 메인 UI      */}
      {/* ----------------------- */}
      <div className="flex-1 px-6 py-2">
        <div className="h-4"></div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* 아이디 */}
          <div>
            <label className="block text-sm font-bold text-gray-600 mb-2">
              아이디
            </label>
            <input
              type="text"
              name="userId"
              value={formData.userId}
              onChange={handleChange}
              onFocus={() => setFocusedField("userId")}
              onBlur={() => setFocusedField("")}
              placeholder="아이디"
              className={`w-full px-4 py-4 border-2 text-base rounded-2xl bg-white transition-all focus:outline-none ${
                errors.userId
                  ? "border-red-400 bg-red-50"
                  : focusedField === "userId"
                  ? "border-teal-400 shadow-lg shadow-teal-100"
                  : "border-gray-200 hover:border-gray-300"
              }`}
            />
            {errors.userId && (
              <p className="text-sm text-red-500 mt-2">{errors.userId}</p>
            )}
          </div>

          {/* 비밀번호 */}
          <div>
            <label className="block text-sm font-bold text-gray-600 mb-2">
              비밀번호
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                placeholder="비밀번호"
                onChange={handleChange}
                onFocus={() => setFocusedField("password")}
                onBlur={() => setFocusedField("")}
                className={`w-full px-4 py-4 border-2 text-base rounded-2xl bg-white transition-all focus:outline-none ${
                  errors.password
                    ? "border-red-400 bg-red-50"
                    : focusedField === "password"
                    ? "border-teal-400 shadow-lg shadow-teal-100"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              />
              {/* 비밀번호 표시/숨기기 아이콘 변경 */}
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? (
                  // 눈을 가린 아이콘 (숨기기)
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                    />
                  </svg>
                ) : (
                  // 눈을 뜬 아이콘 (보기)
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                  </svg>
                )}
              </button>
            </div>
            {errors.password && (
              <p className="text-sm text-red-500 mt-2">{errors.password}</p>
            )}
          </div>

          {/* 로그인 버튼 */}
          <button
            type="submit"
            disabled={!isFormValid}
            className={`w-full py-4 rounded-2xl text-white font-bold transition-all ${
              isFormValid
                ? "shadow-lg hover:shadow-xl active:scale-95"
                : "bg-gray-200 text-gray-400 cursor-not-allowed"
            }`}
            style={{ backgroundColor: isFormValid ? "rgb(0, 224, 198)" : "" }}
          >
            로그인
          </button>
        </form>

        {/* 🔹 OR 구분선   */}
        <div className="mt-10 mb-6 flex items-center">
          {" "}
          {/* mt-12 mb-8 -> mt-10 mb-6으로 조정 */}
          <div className="flex-1 h-[1px] bg-gray-200"></div>
          <span className="mx-4 text-gray-400">or</span>
          <div className="flex-1 h-[1px] bg-gray-200"></div>
        </div>

        {/* 카카오 로그인 */}
        <button
          onClick={handleKakaoLogin}
          className="w-full py-4 rounded-2xl shadow border flex items-center justify-center gap-3 bg-[#FEE500] hover:brightness-95 transition"
        >
          {/* ✅ 카카오 공식 스타일 SVG 아이콘 */}
          <svg
            width="22"
            height="22"
            viewBox="0 0 512 512"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill="#191600"
              d="M255.8 80C141.3 80 48 152.1 48 240c0 58.4 40.8 109.1 101.5 136.7l-20.3 75.6a4.8 4.8 0 0 0 7.3 5.2l90.1-60.1c9.5 1.4 19.3 2.2 29.2 2.2 114.5 0 207.8-72.1 207.8-159.6S370.3 80 255.8 80z"
            />
          </svg>

          <span className="font-medium text-gray-800 text-sm">
            카카오로 로그인
          </span>
        </button>

        {/* 구글 로그인 */}
        <button
          onClick={handleGoogleLogin}
          className="w-full py-4 rounded-2xl shadow border flex items-center justify-center gap-3 bg-white mt-4"
        >
          <img
            src="https://developers.google.com/identity/images/g-logo.png"
            alt="google-logo"
            className="h-5"
          />
          <span className="font-medium text-gray-700 text-sm">
            Google로 로그인
          </span>
        </button>

        <p className="text-left text-xs text-gray-400 px-2 mt-2">
          SNS 로그인 시 회원가입이 자동으로 진행됩니다.
        </p>

        <p className="text-center text-sm text-gray-500 mt-8">
          아직 가입하지 않으셨나요?{" "}
          <button
            onClick={handleSignUp}
            type="button"
            className="font-bold"
            style={{ color: "rgb(0, 224, 198)" }}
          >
            가입하기
          </button>
        </p>
      </div>

      {/* ========================= */}
      {/* 🔹 성별 선택 팝업 모달 */}
      {/* ========================= */}
      {showGenderModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-[85%] max-w-sm bg-white rounded-2xl p-6 shadow-xl">
            <h2 className="text-lg font-bold text-gray-900 mb-2">
              성별을 선택해 주세요
            </h2>
            <p className="text-xs text-gray-500 mb-5">
              맞춤형 통계를 제공하기 위해 한 번만 선택하시면 됩니다.
            </p>

            <div className="flex gap-3 mb-6">
              <button
                type="button"
                onClick={() => setGender("1")}
                className={`flex-1 py-3 rounded-xl border text-sm font-medium ${
                  gender === "1"
                    ? "border-teal-400 bg-teal-50 text-teal-600"
                    : "border-gray-200 text-gray-700"
                }`}
              >
                여성
              </button>
              <button
                type="button"
                onClick={() => setGender("0")}
                className={`flex-1 py-3 rounded-xl border text-sm font-medium ${
                  gender === "0"
                    ? "border-teal-400 bg-teal-50 text-teal-600"
                    : "border-gray-200 text-gray-700"
                }`}
              >
                남성
              </button>
            </div>

            <button
              type="button"
              onClick={handleGenderSubmit}
              className={`w-full py-3 rounded-2xl text-sm font-semibold text-white ${
                gender
                  ? "bg-teal-400 hover:bg-teal-500 active:scale-95 transition"
                  : "bg-gray-300 cursor-not-allowed"
              }`}
              disabled={gender === ""}
            >
              저장하고 계속하기
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
